import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { Router as RouterLang, ServerStatusLanguage } from '../../../../../i18n'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataList, DataListItem } from '../../Molecules/DataList/DataList'
import { getServerStatus } from '../../../../React/Common/API/ServerStatusAPI'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { ServerStatusItem, ServerStatusResponse, ServerStatusRow } from '../../../../NonReact/Types/ServerStatusTypes'
import IconButton, { IconButtonType } from '../../../../React/Common/Components/IconButton'
import { IconName } from '../../../../React/Common/Components/Icon'

/** Stable React key for the synthetic {@code Application Properties} datalist section row. */
const SERVER_STATUS_APPLICATION_PROPERTIES_SECTION_KEY: string = 'server-status-application-properties-section'

/**
 * Routed application server status view.
 *
 * <p>The view is wrapped in {@code server-status-page-container} (max-width 1800px, centred). The main title uses
 * the shared {@code PageTitle} molecule inside {@code admin-list-container}, matching other routed admin-style pages,
 * with a refresh control that reloads status from the API. The data list sits in {@code server-status-page-content}
 * for spacing below the title (see router SCSS). When status rows are present,
 * an {@code Application Properties} section is inserted as the first {@code router-datalist-section} row (above the
 * application public URL from the API).
 *
 * @returns Application status page content.
 */
export const ServerStatus: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(ServerStatusLanguage.NAME)

    const [serverStatusRows, setServerStatusRows]: ReactState<readonly ServerStatusRow[]> = useState<
        readonly ServerStatusRow[]
    >([])
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const loadServerStatus = useCallback((): void => {
        setIsLoading(true)
        getServerStatus()
            .then((response: Nullable<ServerStatusResponse>): void => {
                if (!isMounted.current) return

                const rows: readonly ServerStatusRow[] =
                    response?.Data?.Items?.map(
                        (item: ServerStatusItem, index: number): ServerStatusRow => ({
                            key: `${item.Caption}-${index}`,
                            caption: item.Caption,
                            value: item.Value,
                            isSection: item.Section,
                            emphasiseError: item.EmphasiseError === true
                        })
                    ) ?? []

                setServerStatusRows(rows)
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setIsLoading(false)
            })
    }, [])

    useEffect((): void => {
        loadServerStatus()
    }, [loadServerStatus])

    const dataListItems: readonly DataListItem[] = useMemo((): readonly DataListItem[] => {
        const rows: readonly DataListItem[] = serverStatusRows.map(
            ({ key, caption, value, isSection, emphasiseError }: ServerStatusRow): DataListItem => ({
                key,
                label: caption,
                value,
                isSection,
                emphasiseError
            })
        )

        if (rows.length === 0) return rows

        const applicationPropertiesSection: DataListItem = {
            key: SERVER_STATUS_APPLICATION_PROPERTIES_SECTION_KEY,
            label: language.get(ServerStatusLanguage.ServerStatus_heading_applicationProperties),
            isSection: true
        }

        return [applicationPropertiesSection, ...rows]
    }, [serverStatusRows, language])

    return (
        <div className='server-status-page-container'>
            <div className='server-status-page admin-list-container'>
                <div className='server-status-page-title-row'>
                    <PageTitle title={RouterLang.PageTitle_status} />
                    <IconButton
                        customClassName='server-status-refresh-button'
                        iconName={IconName.REFRESH}
                        type={IconButtonType.BUTTON}
                        title={language.get(ServerStatusLanguage.ServerStatus_action_refresh)}
                        onClick={loadServerStatus}
                        isEnabled={!isLoading}
                    />
                </div>
                <div className='server-status-page-content'>
                    <DataList items={dataListItems} isLoading={isLoading} />
                </div>
            </div>
        </div>
    )
}
